import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SelectableCard } from '../components/onboarding/SelectableCard';
import { SetupScaffold } from '../components/onboarding/SetupScaffold';
import { SETUP_ROLES, SETUP_ROLE_KEY } from '../components/onboarding/accountSetup';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';

/**
 * Cuestionario post-registro ("Account setup"), paso 1: rol del usuario.
 * Réplica del frame de Figma (node 14:252) reutilizando tokens del DS v3
 * (colores/tipografía/radios), como el resto del onboarding (ADR-694/704).
 *
 * - Selección de UNA sola opción (single-select): tocar otra reemplaza la anterior.
 * - "Siguiente" queda deshabilitado hasta elegir un rol; al continuar guarda el rol
 *   y va al paso 2 (`/configuracion-cuenta/categoria`).
 * - Mobile: pantalla completa; desktop (`lg:`): columna centrada (no un simple
 *   escalado — se recompone y limita el ancho).
 *
 * Fue la pantalla a la que caían los usuarios recién registrados (por Google o por
 * el form), vía el redirect de `ProtectedRoute` (ADR-710). Hoy ese redirect volvió
 * al alta clásica (`/onboarding`) y este flujo está en cuarentena bajo
 * `/onboarding-nuevo` — ver `onboardingV2Routes.ts` y
 * ADR-2026-07-31-onboarding-nuevo-en-cuarentena.
 */
export const AccountSetup = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState<string | null>(null);

  // Necesita sesión (viene post-login). Si no hay, vuelve a la bienvenida.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  const handleNext = () => {
    if (!role) return;
    try {
      localStorage.setItem(SETUP_ROLE_KEY, role);
    } catch {}
    navigate(ONBOARDING_V2.setupCategoria);
  };

  return (
    <SetupScaffold
      footer={
        <button
          type="button"
          onClick={handleNext}
          disabled={!role}
          className="flex w-full items-center justify-center rounded-[12px] bg-[#18181B] p-4 text-[16px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] disabled:cursor-not-allowed disabled:bg-[#9F9FA9] md:w-auto md:px-10"
        >
          Siguiente
        </button>
      }
    >
      <h1 className="text-ds-heading-xl text-[#18181B] md:text-center">
        Primero, conozcámonos <br className="md:hidden" />¿Cuál es tu rol?
      </h1>

      {/* Desktop: 3 por fila; la 4ta (última) baja a la 2da fila y queda centrada
          (columna del medio de 3). Mobile: 2×2. */}
      <div
        className="grid grid-cols-2 gap-4 md:grid-cols-3 md:[&>*:last-child]:col-start-2"
        role="radiogroup"
        aria-label="Elegí tu rol"
      >
        {SETUP_ROLES.map((r) => (
          <SelectableCard
            key={r.id}
            label={r.label}
            image={r.image}
            selected={role === r.id}
            onClick={() => setRole(r.id)}
            role="radio"
          />
        ))}
      </div>
    </SetupScaffold>
  );
};

export default AccountSetup;
